'use client';

import { type UserPortfolioSnapshotWire, useAuth } from '@morfi/backend-sdk-v2/client';
import { D6_SCALE, getUsdcMint } from '@/config/solana';
import { useUserPortfolioSnapshotQuerySelect } from '@/hooks/positions/user-portfolio/query-select';
import { useActiveNetwork } from '@/lib/network/use-active-network';
import type { WalletBalancesResult } from '@/queries/wallet-balance';

/**
 * The viewer's USDC per wallet, selected from the indexed portfolio snapshot
 * that the app shell keeps live over NATS — no Solana RPC account reads. Only
 * the signed-in user's own wallets are covered.
 */
export function useIndexedWalletBalances(addresses: readonly string[]): WalletBalancesResult {
  const { user } = useAuth();
  const quoteMint = getUsdcMint(useActiveNetwork());
  const select = (snapshot: UserPortfolioSnapshotWire | null): Map<string, bigint> | undefined =>
    snapshot === null ? undefined : quoteBalancesByWallet(snapshot, addresses, quoteMint);
  const query = useUserPortfolioSnapshotQuerySelect(user?.id ?? '', select);

  const perWalletRaw = query.data ?? new Map<string, bigint>();
  const perWallet = new Map<string, number>();
  let total = 0;
  for (const [wallet, raw] of perWalletRaw) {
    const display = Number(raw) / D6_SCALE;
    perWallet.set(wallet, display);
    total += display;
  }
  const requested = addresses.length > 0;
  return {
    perWallet,
    perWalletRaw,
    total,
    isLoading: requested && query.data === undefined && !query.isError,
    isError: requested && query.isError,
  };
}

function quoteBalancesByWallet(
  snapshot: UserPortfolioSnapshotWire,
  addresses: readonly string[],
  quoteMint: string,
): Map<string, bigint> {
  // A wallet without an indexed row holds no USDC.
  const balances = new Map<string, bigint>(addresses.map((wallet) => [wallet, BigInt(0)]));
  for (const row of snapshot.quoteBalances) {
    if (row.quoteMint === quoteMint && balances.has(row.wallet)) {
      balances.set(row.wallet, BigInt(row.balanceD6));
    }
  }
  return balances;
}
